'use es6';

import CustomWidgetTags from 'ContentUtils/constants/CustomWidgetTags';
import { PENDING, SUCCEEDED } from 'DesignData/enums/RequestStatus';
import ConflictError from 'DesignData/errors/ConflictError';
import * as client from 'DesignData/api/CustomWidgets';
import { getCustomWidgetsByName, getCustomWidgetsIsMappingInitialized, getCustomWidgetsMappingFetchStatus } from 'DesignData/selectors/customWidgets';
import { CUSTOM_WIDGET_CREATE, CUSTOM_WIDGET_CREATE_FROM_FILE, CUSTOM_WIDGETS_WITH_META_DATA_FETCH, CUSTOM_WIDGETS_MAPPING_FETCH, CUSTOM_WIDGET_SAVE_BY_PATH, CUSTOM_WIDGET_SAVE_BY_PATH_REQUEST } from 'DesignData/actions/ActionTypes';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
const getCustomWidgetBuffer = customWidgetBuffersSelectors.makeGetCustomWidgetBuffer();
const getCustomWidgetBufferPayload = customWidgetBuffersSelectors.makeGetCustomWidgetBufferPayload();
const getRequestStatus = customWidgetBuffersSelectors.makeGetRequestStatus();
/**
 * Create new custom widget.
 */

export function create(customWidgetDetails) {
  const {
    request,
    receive,
    error
  } = create;
  return (dispatch, getState) => {
    dispatch(request());
    const state = getState();
    const {
      name
    } = customWidgetDetails;
    const existingCustomWidget = getCustomWidgetsByName(state)[name];

    if (existingCustomWidget) {
      const conflictError = new ConflictError('Conflict with existing custom widget');
      conflictError.customWidgetName = name;
      dispatch(error(conflictError));
      return;
    }

    const newCustomWidgetObject = {
      name,
      fields: [],
      source: ''
    };
    client.create(newCustomWidgetObject).then(customWidget => {
      dispatch(receive(customWidget));
    }).catch(__error => {
      dispatch(error(__error));
    });
  };
}

create.request = function request() {
  return {
    type: CUSTOM_WIDGET_CREATE
  };
};

create.receive = function receive(customWidget) {
  return {
    type: CUSTOM_WIDGET_CREATE,
    customWidget
  };
};

create.error = function error(__error) {
  return {
    type: CUSTOM_WIDGET_CREATE,
    error: __error
  };
};
/**
 * Finder sync middleware action
 */


export function createFromFile(customWidget) {
  return {
    type: CUSTOM_WIDGET_CREATE_FROM_FILE,
    customWidget
  };
}
/*
 * GET designmanager/v1/modules/builtin-mapping
 */

export function fetchMapping() {
  const {
    request,
    receive,
    error
  } = fetchMapping;
  return (dispatch, getState) => {
    const state = getState();

    if (getCustomWidgetsIsMappingInitialized(state)) {
      return;
    }

    const requestStatus = getCustomWidgetsMappingFetchStatus(state);

    if (requestStatus === PENDING || requestStatus === SUCCEEDED) {
      return;
    }

    dispatch(request());
    client.fetchMapping().then(mapping => {
      dispatch(receive(mapping));
    }).catch(err => {
      dispatch(error(err));
    });
  };
}

fetchMapping.request = function request() {
  return {
    type: CUSTOM_WIDGETS_MAPPING_FETCH
  };
};

fetchMapping.receive = function receive(mapping) {
  return {
    type: CUSTOM_WIDGETS_MAPPING_FETCH,
    mapping
  };
};

fetchMapping.error = function error(err) {
  return {
    type: CUSTOM_WIDGETS_MAPPING_FETCH,
    error: err
  };
};
/*
 * GET  designmanager/v1/custom-widgets
 */


export function fetchCustomWidgets() {
  return dispatch => {
    dispatch(fetchCustomWidgets.request());
    client.fetchCustomWidgets({
      limit: 100
    }).then(customWidgets => {
      dispatch(fetchCustomWidgets.receive(customWidgets));
    }).catch(error => {
      dispatch(fetchCustomWidgets.error(error));
    });
  };
}

fetchCustomWidgets.request = function request() {
  return {
    type: CUSTOM_WIDGETS_WITH_META_DATA_FETCH
  };
};

fetchCustomWidgets.receive = function receive(customWidgets) {
  const valueToKey = value => {
    return Object.keys(CustomWidgetTags).find(tag => CustomWidgetTags[tag] === value);
  };

  const processedCustomWidgets = customWidgets.map(customWidget => {
    //  TODO: remove all contentTags/tags processing when tags is deprecated

    /* Convert tags to contentTags, but if it was previously added, don't add a duplicate. Then, merge the lists. */
    // Filter out invalid contentTags (introduced from earlier dev)
    const filteredContentTags = (customWidget.contentTags || []).filter(tag => {
      const isCustomWidgetTagKey = Object.keys(CustomWidgetTags).includes(tag.name);
      const isUserTag = tag.source === 'USER';
      return !(isUserTag && !isCustomWidgetTagKey);
    }); //  Allow tags with same `name` but different `source` to both exist in the Set

    const existingContentTags = new Set();
    filteredContentTags.map(({
      name,
      source
    }) => `${name}:${source}`).forEach(el => existingContentTags.add(el));
    const filteredUserTags = (customWidget.tags || []).filter(tag => {
      const isCustomWidgetTagValue = Object.values(CustomWidgetTags).includes(tag);
      return isCustomWidgetTagValue;
    });
    const convertedTags = filteredUserTags.filter(tag => {
      const name = valueToKey(tag); //  `tags` are defined by the user, therefore source can only be `USER`

      return !existingContentTags.has(`${name}:USER`);
    }).map(tag => ({
      name: valueToKey(tag),
      source: 'USER'
    }));
    return Object.assign({}, customWidget, {
      contentTags: [...filteredContentTags, ...convertedTags]
    });
  });
  return {
    type: CUSTOM_WIDGETS_WITH_META_DATA_FETCH,
    customWidgets: processedCustomWidgets
  };
};

fetchCustomWidgets.error = function error(err) {
  return {
    type: CUSTOM_WIDGETS_WITH_META_DATA_FETCH,
    error: err
  };
};

export function saveByPath(customWidgetData) {
  const {
    id: customWidgetId
  } = customWidgetData;
  const {
    request,
    receive,
    error
  } = saveByPath;
  return (dispatch, getState) => {
    const currentState = getState();
    const {
      isSaving,
      isPublishing
    } = getRequestStatus(currentState, {
      customWidgetId
    });

    if (isSaving || isPublishing) {
      return null;
    }

    const payload = getCustomWidgetBufferPayload(currentState, {
      customWidgetId
    });
    dispatch(request(customWidgetId, payload));
    return client.saveByPath(payload.path, payload).then(customWidget => {
      dispatch(receive(customWidgetId, customWidget, payload));
    }).catch(_error => {
      const {
        css,
        js
      } = getCustomWidgetBuffer(currentState, {
        customWidgetId
      });
      dispatch(error(customWidgetId, _error, {
        css,
        js
      }));
    });
  };
}

saveByPath.request = (customWidgetId, customWidgetBuffer) => {
  return {
    type: CUSTOM_WIDGET_SAVE_BY_PATH_REQUEST,
    customWidgetId,
    customWidgetBuffer
  };
};

saveByPath.receive = (customWidgetId, customWidget, customWidgetBuffer) => {
  return {
    type: CUSTOM_WIDGET_SAVE_BY_PATH,
    customWidgetId,
    customWidget,
    customWidgetBuffer
  };
};

saveByPath.error = (customWidgetId, error, customWidgetData) => {
  return {
    type: CUSTOM_WIDGET_SAVE_BY_PATH,
    customWidgetId,
    error,
    customWidgetData
  };
};